// App.js
import React, {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  createBrowserRouter,
  Routes,
  Route,
  useLocation,
  useParams,
  Link,
  Navigate,
  RouterProvider,
} from "react-router-dom";

import Sidebar from "./components/layout/Sidebar";

import ProtectedRoute from "./components/auth/ProtectedRoute";
import ModuleRuntimeBoundary from "./components/auth/ModuleRuntimeBoundary";
import SessionEntry from "./components/auth/SessionEntry";
import TenantRolloutBoundary from "./components/auth/TenantRolloutBoundary";
import Navbar from "./components/layout/Navbar";
import {
  reconcileNavigationForViewport,
  resolveNavigationPresentation,
  resolveShellHomePath,
  SHELL_NAVIGATION_COLLAPSED_KEY,
  SHELL_DRAWER_MEDIA_QUERY,
} from "./components/layout/shellPolicy";
// Do not import the design-system barrel from the shell: CSS imported by its
// unused module exports would otherwise become part of the initial bundle.
// The shell uses only these three primitives before an authenticated route
// selects its own module surface.
import { ErrorState, LoadingState } from "./design-system/components/StatePanel";
import { ProductThemeProvider } from "./design-system/ProductThemeProvider";
import { moduleExperienceForPath } from "./components/moduleExperience/moduleExperienceRegistry";
import { AccountConnectionScopeProvider } from "./components/moduleExperience/AccountConnectionScopeContext";
import ConnectionScopeGate from "./components/moduleExperience/ConnectionScopeGate";
import { resolveModuleRoute } from "./components/navigation/moduleRoutes";
import { isTemporarilyHiddenModule } from "./components/navigation/moduleVisibility";
import BrowserUrlPrivacyGuard from "./components/navigation/BrowserUrlPrivacyGuard";
import { LanguageProvider, useLanguage } from "./localization/LanguageContext";
import { DisplayTimeZoneProvider } from "./dateTime/DisplayTimeZoneContext";
import { SESSION_CHANGED_EVENT } from "./components/pages/backend_url";
import {
  applyThemePreference,
  readThemePreference,
  themeFromStorageEvent,
} from "./theme/themePreference";
import "./App.css";
import "./design-system/uiExperience.css";

// Keep the authenticated shell fast as the larger business modules evolve.
// Each route is downloaded only when a user is authorized and navigates to it.
const Dashboard = lazy(() => import(/* webpackChunkName: "module-dashboard" */ "./components/pages/dashboard/DashboardPage.js"));
// Public entry and invitation acceptance are isolated routes. Keeping them
// outside the authenticated shell's first JavaScript download reduces the
// time to an interactive signed-in workspace without changing either route.
const Login = lazy(() => import(/* webpackChunkName: "public-login" */ "./components/pages/login/Login"));
const InvitationAcceptance = lazy(() => import(/* webpackChunkName: "public-invitation" */ "./components/pages/invitations/InvitationAcceptance"));
// These render only after a protected route has been selected. Keeping their
// module-header and connection-control styles out of the initial shell avoids
// sending workspace-only CSS to the public sign-in path.
const ModuleWorkspace = lazy(() => import("./design-system/components/ModuleWorkspace").then((module) => ({ default: module.ModuleWorkspace })));
const AccountConnectionScopeControl = lazy(() => import("./components/moduleExperience/AccountConnectionScopeControl"));
// Time-zone selection is only meaningful inside an authenticated module
// header. Keep the full control out of the public/startup path; the existing
// route Suspense boundary supplies a stable loading state before it renders.
const DisplayTimeZoneControl = lazy(() => import(/* webpackChunkName: "shell-timezone-control" */ "./dateTime/DisplayTimeZoneControl"));
const CallCenterInterface = lazy(() => import(/* webpackChunkName: "module-call-center" */ "./components/pages/callCenterInterface/CallCenterInterface"));
const VonHubInterface = lazy(() => import(/* webpackChunkName: "module-vonage-hub" */ "./components/pages/vonHubInterface/VonHubInterface"));
const CustomerSearch = lazy(() => import(/* webpackChunkName: "module-customer-search" */ "./components/pages/customerSearch/CustomerSearch"));
const CallsInfo = lazy(() => import(/* webpackChunkName: "module-call-history" */ "./components/pages/vonageCallsInfo/VonageCallsInfo"));
const LocationsInfo = lazy(() => import(/* webpackChunkName: "module-locations" */ "./components/pages/location/Location"));
const QueueSetup = lazy(() => import(/* webpackChunkName: "module-queues" */ "./components/pages/queueSetup/QueueSetup"));
const HubSpotInfo = lazy(() => import(/* webpackChunkName: "module-hubspot-email" */ "./components/pages/hubSpotEmailsInfo/HubSpotEmailsInfo"));
const UsersSetup = lazy(() => import(/* webpackChunkName: "module-users" */ "./components/pages/usersSetup/UserSetup.js"));
const AccessSetup = lazy(() => import(/* webpackChunkName: "module-access" */ "./components/pages/accessSetup/AccessSetup.js"));
const AgentsSetup = lazy(() => import(/* webpackChunkName: "module-agents" */ "./components/pages/agentsSetup/AgentsSetup.js"));
const UnifiedCRM = lazy(() => import(/* webpackChunkName: "module-unified-crm" */ "./components/pages/unifiedCrm/UnifiedCrm"));
const EmailTickets = lazy(() => import(/* webpackChunkName: "module-email-tickets" */ "./components/pages/emailTickets/EmailTickets"));
const OperationalPlaybooksWorkspace = lazy(() => import(/* webpackChunkName: "module-operational-playbooks" */ "./components/pages/operationalPlaybooks/OperationalPlaybooksWorkspace"));
const HubSpotArchive = lazy(() => import(/* webpackChunkName: "module-hubspot-archive" */ "./components/pages/hubSpotArchive/HubSpotArchive"));
const CatalogSetup = lazy(() => import(/* webpackChunkName: "module-catalog" */ "./components/pages/catalogSetup/CatalogSetup"));
const CustomerInfo360 = lazy(() => import(/* webpackChunkName: "module-customer-360" */ "./components/pages/customerInfo360/CustomerInfo360"));
const AccountControlCenter = lazy(() => import(/* webpackChunkName: "module-account" */ "./components/pages/accountSetup/AccountControlCenter"));
const ReportsWorkspace = lazy(() => import(/* webpackChunkName: "module-reports" */ "./components/pages/reports/ReportsWorkspace"));
const QualityAssuranceWorkspace = lazy(() => import(/* webpackChunkName: "module-quality-assurance" */ "./components/pages/qualityAssurance/QualityAssuranceWorkspace"));
const ActionCenterWorkspace = lazy(() => import(/* webpackChunkName: "module-action-center" */ "./components/pages/actionCenter/ActionCenterWorkspace"));
const AutomationStudioWorkspace = lazy(() => import(/* webpackChunkName: "module-automation-studio" */ "./components/pages/automationStudio/AutomationStudioWorkspace"));

// The desktop workspace starts with a readable navigation rail. The choice is
// persisted as a user-owned preference and survives a secure account switch,
// so switching accounts never makes the navigation appear to disappear.
const MOBILE_NAVIGATION_QUERY = SHELL_DRAWER_MEDIA_QUERY;
const PRIMARY_NAVIGATION_ID = "crm-primary-navigation";
const PRIMARY_NAVIGATION_TOGGLE_ID = "crm-primary-navigation-toggle";
const NAVIGATION_FOCUSABLE_ELEMENTS = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

function UnknownModuleRoute({ requestedPath = "" }) {
  const { copy } = useLanguage();
  return (
    <section className="crm-route-error" aria-label="Unknown module route">
      <ErrorState
        title={copy("This module link is not registered")}
        description={
          requestedPath
            ? copy("No CRM module is registered for {requestedPath}. Use the navigation menu or return to Dashboard.", { requestedPath })
            : copy("Use the navigation menu or return to Dashboard.")
        }
        actions={(
          <div className="crm-route-error__actions">
            <Link to="/Dashboard">{copy("Open Dashboard")}</Link>
            <Link to="/AccountSetup">{copy("Open Account Setup")}</Link>
          </div>
        )}
      />
    </section>
  );
}

function PublicEntryLoading() {
  return (
    <main className="crm-router-error" aria-live="polite" aria-busy="true">
      <LoadingState title="Opening secure workspace" description="Preparing the verified sign-in experience." />
    </main>
  );
}

function LegacyModuleRedirect() {
  const { moduleName = "" } = useParams();
  const destination = resolveModuleRoute(moduleName, `/CallCenter/${moduleName}`);
  return destination
    ? <Navigate to={destination} replace />
    : <UnknownModuleRoute requestedPath={`/CallCenter/${moduleName}`} />;
}

function safeJsonParse(raw, fallback = null) {
  try {
    return raw ? JSON.parse(raw) : fallback;
  } catch (err) {
    console.error("Invalid JSON in storage:", err);
    return fallback;
  }
}

function getLoggedUser() {
  const user = safeJsonParse(sessionStorage.getItem("user_token"), null);

  if (!user || typeof user !== "object") return null;

  // Required user fields check. Some login payloads use userEmail, some use email.
  if (!user.role && !user.userEmail && !user.email) return null;

  return user;
}

function getInitialTheme() {
  return readThemePreference();
}

function readDesktopNavigationPreference() {
  try {
    const stored = localStorage.getItem(SHELL_NAVIGATION_COLLAPSED_KEY);
    return stored === null ? false : stored === "true";
  } catch {
    return false;
  }
}

function getInitialNavigationCollapsed() {
  try {
    // Mobile always starts with a closed drawer, but preserve the independent
    // desktop preference so returning to a wide viewport restores the rail
    // state the operator deliberately chose.
    if (window.matchMedia?.(MOBILE_NAVIGATION_QUERY)?.matches) return true;
  } catch {
    // Storage fallback below keeps a blocked matchMedia implementation safe.
  }

  return readDesktopNavigationPreference();
}

function AppShell() {
  const { copy } = useLanguage();
  const [collapsedFlag, setCollapsedFlag] = useState(getInitialNavigationCollapsed);
  const [theme, setTheme] = useState(getInitialTheme);
  const [activeUser, setActiveUser] = useState(() => getLoggedUser() || {});
  const mainContentRef = useRef(null);
  const desktopNavigationPreferenceRef = useRef(readDesktopNavigationPreference());

  const location = useLocation();

  useEffect(() => {
    applyThemePreference(theme);
  }, [theme]);

  useEffect(() => {
    const media = window.matchMedia?.(MOBILE_NAVIGATION_QUERY);
    if (!media) return undefined;

    const reconcileNavigationViewport = (event) => {
      const next = reconcileNavigationForViewport({
        isMobile: event.matches,
        currentCollapsed: collapsedFlag,
        desktopCollapsed: desktopNavigationPreferenceRef.current,
      });

      desktopNavigationPreferenceRef.current = next.desktopCollapsed;
      setCollapsedFlag(next.collapsed);
    };

    if (!media.matches) {
      desktopNavigationPreferenceRef.current = collapsedFlag;
    }

    media.addEventListener?.("change", reconcileNavigationViewport);
    return () => media.removeEventListener?.("change", reconcileNavigationViewport);
  }, [collapsedFlag]);

  useEffect(() => {
    try {
      if (!window.matchMedia?.(MOBILE_NAVIGATION_QUERY)?.matches) {
        localStorage.setItem(SHELL_NAVIGATION_COLLAPSED_KEY, String(collapsedFlag));
      }
    } catch {
      // A blocked preference store must not make navigation unusable.
    }
  }, [collapsedFlag]);

  useEffect(() => {
    const onStorage = (event) => {
      setTheme((current) => themeFromStorageEvent(event, current));
    };

    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  useEffect(() => {
    const refreshActiveUser = (event) => {
      setActiveUser(event?.detail?.authenticated === false ? {} : getLoggedUser() || {});
    };
    window.addEventListener(SESSION_CHANGED_EVENT, refreshActiveUser);
    return () => window.removeEventListener(SESSION_CHANGED_EVENT, refreshActiveUser);
  }, []);

  const handleToggleTheme = useCallback((nextTheme) => {
    setTheme((current) => {
      if (nextTheme === "dark" || nextTheme === "light") return nextTheme;

      return current === "dark" ? "light" : "dark";
    });
  }, []);

  const handleLoginSuccess = useCallback(() => {
    const user = getLoggedUser();

    if (!user) {
      console.warn("Login success called, but user_token is missing");
      localStorage.removeItem("auth");
      return;
    }
    setActiveUser(user);
  }, []);

  const handleSessionAuthenticated = useCallback(() => {
    setActiveUser(getLoggedUser() || {});
    window.requestAnimationFrame(() => {
      mainContentRef.current?.focus({ preventScroll: true });
    });
  }, []);

  const closeMobileNavigation = useCallback(() => {
    try {
      if (window.matchMedia?.(MOBILE_NAVIGATION_QUERY)?.matches) {
        setCollapsedFlag(true);
      }
    } catch {}
  }, []);

  const routes = useMemo(
    () => [
      {
        path: "/Dashboard",
        name: "Dashboard",
        element: <Dashboard />,
        // Both CRM activity and email analytics use tenant/row scoped APIs.
        tenantReady: true,
      },
      {
        path: "/CallCenterInterface",
        name: "Call Center",
        element: <CallCenterInterface />,
        // Capture identity, workspace, timestamps and ticket IDs are server-derived.
        tenantReady: true,
      },
      { path: "/Von-HubInterface", name: "Vonage Hub", element: <VonHubInterface />, integrationRequired: "Vonage" },
      {
        path: "/CustomerSearch",
        name: "Customer Search",
        element: <CustomerSearch />,
        // Searches only the verified workspace and the actor's customer data scope.
        tenantReady: true,
      },
      {
        path: "/CallsInfo",
        name: "Calls",
        element: <CallsInfo />,
        // The module now owns its integration readiness state and reads only
        // normalized records within the verified workspace boundary.
        tenantReady: true,
      },
      {
        path: "/LocationsInfo",
        name: "Locations",
        element: <LocationsInfo />,
        // Uses the tenant-scoped location master and server-derived authority.
        tenantReady: true,
      },
      {
        path: "/QueueSetup",
        name: "Queue Setup",
        element: <QueueSetup />,
        tenantReady: true,
      },
      { path: "/HubSpotInfo", name: "HubSpot Email", element: <HubSpotInfo />, integrationRequired: "HubSpot" },
      {
        path: "/UsersSetup",
        name: "Users Setup",
        element: <UsersSetup />,
        tenantReady: true,
      },
      {
        path: "/AccessSetup",
        name: "Access Setup",
        element: <AccessSetup />,
        // This screen now uses only membership-authorized, tenant-scoped APIs.
        tenantReady: true,
      },
      {
        path: "/AgentsSetup",
        name: "Agents Setup",
        element: <AgentsSetup />,
        tenantReady: true,
      },
      {
        path: "/CatalogSetup",
        name: "Catalog Setup",
        element: <CatalogSetup />,
        tenantReady: true,
      },
      {
        path: "/CustomerInfo360",
        name: "Customer 360",
        element: <CustomerInfo360 />,
        tenantReady: true,
      },
      {
        path: "/UnifiedCRM",
        name: "Unified CRM",
        element: <UnifiedCRM />,
        tenantReady: true,
      },
      {
        path: "/EmailTickets",
        name: "Email Tickets",
        element: <EmailTickets />,
        // Uses the tenant-, data-scope- and mailbox-policy-scoped v1 API.
        tenantReady: true,
      },
      {
        path: "/OperationalPlaybooks",
        name: "Operational Playbooks",
        element: <OperationalPlaybooksWorkspace />,
        // Guides and acknowledgement evidence always remain bound to one
        // server-authorized email connection.
        tenantReady: true,
      },
      { path: "/HubSpotArchive", name: "HubSpot Archive", element: <HubSpotArchive />, integrationRequired: "HubSpot archive" },
      {
        path: "/AccountSetup",
        name: "Account Setup",
        element: <AccountControlCenter />,
        tenantReady: true,
      },
      {
        path: "/Reports",
        name: "Reports",
        element: <ReportsWorkspace />,
        tenantReady: true,
      },
      {
        path: "/QualityAssurance",
        name: "Quality Assurance",
        element: <QualityAssuranceWorkspace />,
        tenantReady: true,
      },
      {
        path: "/ActionCenter",
        name: "Action Center",
        element: <ActionCenterWorkspace />,
        tenantReady: true,
      },
      {
        path: "/AutomationStudio",
        name: "Automation Studio",
        element: <AutomationStudioWorkspace />,
        tenantReady: true,
      },
      {
        path: "/Audit",
        name: "Audit trail",
        element: <ReportsWorkspace initialTab="audit" auditOnly />,
        tenantReady: true,
      },
    ],
    []
  );

  const isLoginPage = location.pathname === "/";
  const membershipBacked =
    String(activeUser.tenantAuthorizationSource || "").trim().toLowerCase() ===
    "tenant_membership";
  const activeConnectionScopeMode =
    moduleExperienceForPath(location.pathname)?.connectionScope || "none";
  const connectionScopeRequired = activeConnectionScopeMode === "connection_required";
  const homePath = resolveShellHomePath(activeUser);
  const navigationPresentation = resolveNavigationPresentation({
    isMobile: Boolean(window.matchMedia?.(MOBILE_NAVIGATION_QUERY)?.matches),
    collapsed: collapsedFlag,
  });
  const mobileNavigationOpen = navigationPresentation.drawerOpen;
  const navigationMainContentInert = navigationPresentation.inertMainContent;

  useEffect(() => {
    if (isLoginPage) return undefined;

    const frame = window.requestAnimationFrame(() => {
      mainContentRef.current?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [isLoginPage, location.pathname]);

  useEffect(() => {
    const mainContent = mainContentRef.current;

    if (!navigationMainContentInert) {
      document.body?.classList.remove("crm-mobile-navigation-open");
      mainContent?.removeAttribute("inert");
      return undefined;
    }

    const navigation = document.getElementById(PRIMARY_NAVIGATION_ID);
    const navigationToggle = document.getElementById(PRIMARY_NAVIGATION_TOGGLE_ID);
    let focusFrame;

    document.body?.classList.add("crm-mobile-navigation-open");
    mainContent?.setAttribute("inert", "");

    const getFocusableElements = () =>
      Array.from(
        navigation?.querySelectorAll(NAVIGATION_FOCUSABLE_ELEMENTS) || []
      ).filter(
        (element) =>
          !element.hasAttribute("hidden") &&
          element.getAttribute("aria-hidden") !== "true"
      );

    const focusNavigation = () => {
      getFocusableElements()[0]?.focus();
    };

    const handleNavigationKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setCollapsedFlag(true);
        return;
      }

      if (event.key !== "Tab") return;

      const focusable = getFocusableElements();
      if (!focusable.length) {
        event.preventDefault();
        navigation?.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const focusIsInsideNavigation = navigation?.contains(document.activeElement);

      if (!focusIsInsideNavigation) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    focusFrame = window.requestAnimationFrame(focusNavigation);
    document.addEventListener("keydown", handleNavigationKeyDown);

    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener("keydown", handleNavigationKeyDown);
      document.body?.classList.remove("crm-mobile-navigation-open");
      mainContent?.removeAttribute("inert");

      if (navigationToggle?.isConnected) {
        window.requestAnimationFrame(() => navigationToggle.focus());
      }
    };
  }, [collapsedFlag, mobileNavigationOpen, navigationMainContentInert]);

  if (isLoginPage) {
    return (
      <ProductThemeProvider mode={theme} onModeChange={handleToggleTheme}>
        <SessionEntry onAuthenticated={handleSessionAuthenticated}>
          <Suspense fallback={<PublicEntryLoading />}>
            <Login
              onLogin={handleLoginSuccess}
              theme={theme}
              onToggleTheme={handleToggleTheme}
            />
          </Suspense>
        </SessionEntry>
      </ProductThemeProvider>
    );
  }

  return (
    <ProductThemeProvider mode={theme} onModeChange={handleToggleTheme}>
      <ProtectedRoute onAuthenticated={handleSessionAuthenticated}>
      <AccountConnectionScopeProvider
        enabled={connectionScopeRequired}
        required={connectionScopeRequired}
      >
      <DisplayTimeZoneProvider>
      <div
        className="crm-app-shell"
        data-theme={theme}
        data-navigation={collapsedFlag ? "collapsed" : "expanded"}
      >
        <a className="crm-skip-link" href="#crm-main-content">
          Skip to main content
        </a>

        <Navbar
          theme={theme}
          onToggleTheme={handleToggleTheme}
          navigationOpen={!collapsedFlag}
          navigationModalOpen={mobileNavigationOpen}
          onToggleNavigation={() => setCollapsedFlag((value) => !value)}
          homePath={homePath}
        />

        <div className="crm-app-shell__frame">
          <Sidebar
            theme={theme}
            collapsed={collapsedFlag}
            onCollapsedChange={setCollapsedFlag}
            onNavigate={closeMobileNavigation}
            shellControlsMobileFocus
          />

          {navigationPresentation.showBackdrop && (
            <button
              type="button"
              className="crm-shell-backdrop"
              onClick={() => setCollapsedFlag(true)}
              aria-label="Close navigation"
            />
          )}

          <main
            id="crm-main-content"
            ref={mainContentRef}
            className="crm-app-shell__main"
            tabIndex="-1"
          >
            <div className="crm-app-shell__viewport">
              <Suspense
                fallback={(
                  <LoadingState
                    title={copy("Opening module")}
                    description={copy("Preparing your workspace and authorized business tools.")}
                  />
                )}
              >
              <Routes>
                <Route path="/" element={<Navigate to={homePath} replace />} />

                {routes
                  .filter((route) => !isTemporarilyHiddenModule(route.name, route.path))
                  .map((route) => (
                  <Route
                    key={route.path}
                    path={route.path}
                    element={
                      <ModuleWorkspace
                        module={moduleExperienceForPath(route.path)}
                        connectionScopeControl={<AccountConnectionScopeControl />}
                        displayTimeZoneControl={<DisplayTimeZoneControl />}
                        migrationProtected={membershipBacked && !route.tenantReady}
                        rolloutLabel={route.integrationRequired ? "Integration setup" : "Account rollout"}
                      >
                        <ModuleRuntimeBoundary resetKey={route.path} moduleName={route.name}>
                          <ConnectionScopeGate>
                            {route.tenantReady ? (
                              route.element
                            ) : (
                              <TenantRolloutBoundary moduleName={route.name} integrationName={route.integrationRequired}>
                                {route.element}
                              </TenantRolloutBoundary>
                            )}
                          </ConnectionScopeGate>
                        </ModuleRuntimeBoundary>
                      </ModuleWorkspace>
                    }
                  />
                  ))}

                <Route
                  path="/CallCenter/:moduleName"
                  element={<LegacyModuleRedirect />}
                />

                <Route
                  path="*"
                  element={<UnknownModuleRoute requestedPath={location.pathname} />}
                />
              </Routes>
              </Suspense>
            </div>
          </main>
        </div>
      </div>
      </DisplayTimeZoneProvider>
      </AccountConnectionScopeProvider>
      </ProtectedRoute>
    </ProductThemeProvider>
  );
}

function AppRouterRoot() {
  return (
    <BrowserUrlPrivacyGuard>
      <LanguageProvider>
        <AppShell />
      </LanguageProvider>
    </BrowserUrlPrivacyGuard>
  );
}

/*
 * The module boundary preserves the shell when an individual page fails. This
 * root fallback covers the smaller remaining surface (for example, an
 * unexpected shell or route-loader render failure) so operators never land
 * on React Router's developer-facing exception page.
 */
function LocalizedRouterErrorFallback() {
  const { copy } = useLanguage();
  const mode = getInitialTheme();
  return (
    <ProductThemeProvider mode={mode}>
      <main className="crm-router-error" aria-label="CRM recovery">
        <ErrorState
          title={copy("RingOi needs to reopen this workspace")}
          description={copy("The page did not load safely. No customer data, ticket changes, or connection settings were changed. Return to a known workspace or reload the portal.")}
          actions={(
            <div className="crm-route-error__actions">
              <Link to="/AccountSetup" className="crm-button" data-variant="primary" data-size="medium">
                <span className="crm-button__label">{copy("Open Account Setup")}</span>
              </Link>
              <button type="button" className="crm-button" data-variant="secondary" data-size="medium" onClick={() => window.location.reload()}>
                <span className="crm-button__label">{copy("Reload portal")}</span>
              </button>
            </div>
          )}
        />
      </main>
    </ProductThemeProvider>
  );
}

function AppRouterErrorFallback() {
  return (
    <LanguageProvider>
      <LocalizedRouterErrorFallback />
    </LanguageProvider>
  );
}

// A data router gives business forms a supported navigation blocker. The
// routed CRM shell remains unchanged; only the root history owner moves from
// BrowserRouter to RouterProvider.
const appRouter = createBrowserRouter(
  [
    {
      path: "/accept-invitation",
      element: (
        <Suspense fallback={<PublicEntryLoading />}>
          <InvitationAcceptance />
        </Suspense>
      ),
      errorElement: <AppRouterErrorFallback />,
    },
    { path: "*", element: <AppRouterRoot />, errorElement: <AppRouterErrorFallback /> },
  ],
  { future: { v7_relativeSplatPath: true } }
);

export default function App() {
  return <RouterProvider router={appRouter} future={{ v7_startTransition: true }} />;
}
